import { FileTypeEnum } from '$/utils/upload';
import { ImageHdType, VideoSRMode } from '@/store/useAIEnhanceStore';

/**
 * 根据批量素材自身类型推断高清埋点媒体类型，兼容旧数据中未写 type 的 URL。
 */
function getEnhanceTrackMediaType(item?: { type?: FileTypeEnum; url?: string; }) {
  if (item?.type === FileTypeEnum.Video) return FileTypeEnum.Video;
  if (item?.type === FileTypeEnum.Image) return FileTypeEnum.Image;
  return /\.(mp4|mov|m4v)(\?|#|$)/i.test(item?.url || '') ? FileTypeEnum.Video : FileTypeEnum.Image;
}

/**
 * 将 AI 高清模式转换为运行、成功和下载事件共用的埋点参数。
 */
export function getEnhanceTrackParams(options: {
  mediaType: FileTypeEnum | 'image' | 'video';
  imageHdType: ImageHdType;
  videoSRMode?: VideoSRMode;
}) {
  const { mediaType, imageHdType, videoSRMode } = options;
  const isVideo = mediaType === FileTypeEnum.Video || mediaType === 'video';
  const modeValue = isVideo ? videoSRMode : imageHdType;
  const enhanceField = isVideo
    ? { enhanceVideo: modeValue }
    : { enhanceImage: modeValue };

  if (modeValue === ImageHdType.HD || modeValue === VideoSRMode._2K) {
    return {
      ...enhanceField,
      changeToHdType: 'HD',
    };
  }

  if (
    modeValue === ImageHdType.Super
    || modeValue === ImageHdType.AISuper
    || modeValue === VideoSRMode._4K
    || modeValue === VideoSRMode.AI4KPlus
  ) {
    return {
      ...enhanceField,
      changeToHdType: 'Ultra_HD',
    };
  }

  if (modeValue === ImageHdType.Goods || modeValue === VideoSRMode.Goods) {
    return {
      ...enhanceField,
      autoFindType: 'commodity',
    };
  }

  if (modeValue === ImageHdType.Text) {
    return {
      ...enhanceField,
      autoFindType: 'text',
    };
  }

  if (
    modeValue === ImageHdType.Portrait
    || modeValue === ImageHdType.PortraitPro
    || modeValue === VideoSRMode.Portrait
  ) {
    return {
      ...enhanceField,
      autoFindType: 'portrait',
    };
  }

  return enhanceField;
}

/**
 * 批量高清混合图片和视频时，同时补齐 enhance_image 与 enhance_video 字段。
 */
export function getBatchEnhanceTrackParams(options: {
  items: Array<{ type?: FileTypeEnum; url?: string; }>;
  imageHdType: ImageHdType;
  videoSRMode?: VideoSRMode;
}) {
  const { items, imageHdType, videoSRMode } = options;
  const mediaTypes = items.map(getEnhanceTrackMediaType);
  const firstMediaType = mediaTypes[0] || FileTypeEnum.Image;
  const hasImage = mediaTypes.includes(FileTypeEnum.Image);
  const hasVideo = mediaTypes.includes(FileTypeEnum.Video);

  return {
    ...getEnhanceTrackParams({
      mediaType: firstMediaType,
      imageHdType,
      videoSRMode,
    }),
    ...(hasImage ? { enhanceImage: imageHdType } : {}),
    ...(hasVideo ? { enhanceVideo: videoSRMode } : {}),
  };
}
